<script setup lang="ts">
import { ref } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { availableCaregiversApi } from '@/api/available_caregivers';
import { type Caregiver } from '@/api/caregivers';
import { type Shift } from '@/api/shifts';
import { formatCurrency } from '@/utils/currency';
import { getCareerColor } from '@/utils/careers';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ShiftOfferingForm from './shift-offering-form.vue';

const props = defineProps<{
  shift: Shift;
}>();

const headers = [
  { title: 'Nombre', value: 'firstName', sortable: true },
  { title: 'Apellido', value: 'lastName', sortable: true },
  { title: 'Comuna', value: 'communeName' },
  { title: 'Especialidad', value: 'humanCareer', sortable: true },
  { title: 'Acciones', value: 'actions' },
];

const { data: availableCaregivers, isPending } = useQuery({
  queryKey: ['availableCaregivers', { shiftId: props.shift.id }],
  queryFn: () => availableCaregiversApi.index({ shiftId: props.shift.id }),
});

const search = ref('');
const tab = ref('email');
const selectedCaregivers = ref<Caregiver[]>([]);
const showOfferingForm = ref(false);
const offeringSent = ref(false);

const queryClient = useQueryClient();
const { trackShiftEvent } = useAnalytics();
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 10 });

function generateWhatsappMessage(caregiver: Caregiver) {
  const shift = props.shift;
  const message = `Hola ${caregiver.firstName}, te escribo por un turno disponible:\n` +
    '------------------\n' +
    `*Fecha*: ${shift.humanDate}\n` +
    `*Hora*: ${shift.humanTime}\n` +
    `*Paciente*: ${shift.patientFullName}\n` +
    `*Dirección*: ${shift.patientAddress ? shift.patientAddress : 'No disponible'}\n` +
    `*Pago Líquido*: ${formatCurrency(shift.caregiverCost)}\n` +
    '------------------\n' +
    '¿Te interesa? Por favor confirma por este medio.';

  return encodeURIComponent(message);
}

function handleOfferShift(caregiver: Caregiver) {
  trackShiftEvent(AnalyticsEvents.shifts.offered, {
    caregiverId: caregiver.id,
    caregiverCareer: caregiver.career,
  }, props.shift.id);

  const url = `https://wa.me/${caregiver.phoneNumber}?text=${generateWhatsappMessage(caregiver)}`;
  window.open(url, '_blank');
}

function handleOpenOfferingForm() {
  showOfferingForm.value = true;
}

const ALERT_DISMISS_MS = 10_000;

function handleOfferingSuccess() {
  showOfferingForm.value = false;
  offeringSent.value = true;
  selectedCaregivers.value = [];
  queryClient.invalidateQueries({ queryKey: ['shiftOfferings'] });
  setTimeout(() => {
    offeringSent.value = false;
  }, ALERT_DISMISS_MS);
}
</script>
<template>
  <v-card class="w-100">
    <v-toolbar
      color="white"
      flat
    >
      <v-toolbar-title class="font-weight-bold">
        Profesionales disponibles
      </v-toolbar-title>
    </v-toolbar>

    <v-tabs
      v-model="tab"
      color="primary"
      grow
    >
      <v-tab value="email">
        Ofrecer por Email
      </v-tab>
      <v-tab value="whatsapp">
        Contactar por WhatsApp
      </v-tab>
    </v-tabs>

    <v-toolbar
      color="white"
      class="ml-2"
      flat
    >
      <v-spacer />
      <v-text-field
        v-model="search"
        label="Buscar"
        class="mx-4 mb-4 v-col-3"
        prepend-inner-icon="mdi-magnify"
        single-line
        variant="underlined"
        hide-details
      />
    </v-toolbar>

    <v-window v-model="tab">
      <v-window-item value="email">
        <v-data-table
          v-bind="tableProps"
          v-model="selectedCaregivers"
          :headers="headers"
          :header-props="{ class: 'font-weight-bold' }"
          :items="availableCaregivers"
          :search="search"
          :loading="isPending"
          loading-text="Cargando los profesionales disponibles..."
          show-select
          return-object
          item-value="id"
        >
          <template #item.firstName="{ item }">
            {{ item.firstName }}
            <v-icon
              v-if="!item.email"
              color="warning"
              size="small"
              class="ml-1"
            >
              mdi-email-off-outline
            </v-icon>
            <v-tooltip
              v-if="!item.email"
              activator="parent"
              location="bottom"
            >
              Sin email registrado
            </v-tooltip>
          </template>
          <template #item.humanCareer="{ item }">
            <v-chip :color="getCareerColor(item.career)">
              {{ item.humanCareer }}
            </v-chip>
          </template>
          <template #item.data-table-select="{ item, isSelected, toggleSelect }">
            <v-checkbox-btn
              :model-value="isSelected({ value: item, selectable: true })"
              :disabled="!item.email"
              @update:model-value="toggleSelect({ value: item, selectable: true })"
            />
          </template>
        </v-data-table>
      </v-window-item>

      <v-window-item value="whatsapp">
        <v-data-table
          v-bind="tableProps"
          :headers="headers"
          :header-props="{ class: 'font-weight-bold' }"
          :items="availableCaregivers"
          :search="search"
          :loading="isPending"
          loading-text="Cargando los profesionales disponibles..."
        >
          <template #item.humanCareer="{ item }">
            <v-chip :color="getCareerColor(item.career)">
              {{ item.humanCareer }}
            </v-chip>
          </template>
          <template #item.actions="{ item }">
            <v-btn
              v-if="item.phoneNumber"
              icon="mdi-whatsapp"
              size="small"
              color="success"
              @click="handleOfferShift(item)"
            />
          </template>
        </v-data-table>
      </v-window-item>
    </v-window>

    <v-card-actions
      v-if="tab === 'email'"
      class="justify-end px-6 pb-4"
    >
      <v-btn
        v-if="selectedCaregivers.length > 0"
        color="primary"
        variant="outlined"
        @click="handleOpenOfferingForm"
      >
        Enviar Oferta ({{ selectedCaregivers.length }})
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="offeringSent && tab === 'email'"
      type="success"
      class="mx-6 mb-4"
    >
      Oferta enviada exitosamente. Los profesionales recibirán un email.
    </v-alert>

    <v-dialog
      v-model="showOfferingForm"
      max-width="500"
    >
      <shift-offering-form
        :selected-caregivers="selectedCaregivers"
        :shift="shift"
        @close="showOfferingForm = false"
        @success="handleOfferingSuccess"
      />
    </v-dialog>
  </v-card>
</template>
